import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import LegacyButton from "legacy/LegacyButton"
import ContractGroupV2AddBoat from "legacy/contract-groups-v2/ContractGroupV2AddBoat"
import ContractGroupV2Billing from "legacy/contract-groups-v2/ContractGroupV2Billing"
import ContractGroupV2Charges from "legacy/contract-groups-v2/ContractGroupV2Charges"
import ContractGroupV2Deposit from "legacy/contract-groups-v2/ContractGroupV2Deposit"
import ContractGroupV2Documents from "legacy/contract-groups-v2/ContractGroupV2Documents"
import ContractGroupV2FeesElectricDiscounts from "legacy/contract-groups-v2/ContractGroupV2FeesElectricDiscounts"
import ContractGroupV2Itinerary from "legacy/contract-groups-v2/ContractGroupV2Itinerary"
import ContractGroupV2PaymentMethods from "legacy/contract-groups-v2/ContractGroupV2PaymentMethods"
import ContractGroupV2Rate from "legacy/contract-groups-v2/ContractGroupV2Rate"
import ContractLeftTabLayout from "legacy/contract-groups-v2/ContractLeftTabLayout"
import {
  afterSetState,
  buildRoute,
  centsToDollars,
  centsToDollarsRaw,
  collectionKeysToSnakeCase,
  dollarsToCents,
  isBlank,
  isPresent,
  safePercentageDecimal,
} from "legacy/helpers.es6"
import PropTypes from "prop-types"
import React from "react"

// Note: $context is loaded globally
// Note: Amplitude is loaded globally
// Note: jQuery ($) is loaded globally

const TABS = {
  itinerary: "itinerary",
  rate: "rate",
  billing: "billing",
  paymentMethods: "paymentMethods",
  deposit: "deposit",
  charges: "charges",
  fees: "fees",
  documents: "documents",
  contactAndBoat: "contactAndBoat",
}

class ContractGroupV2Controller extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      copyBoats: true,
      activeTab: this.isContract() ? "contactAndBoat" : "itinerary",
      saving: null,
      errorMessage: null,
      form: {
        rate: {},
        rateVisited: false,
        allowDirectPayment: false,
        acceptedPaymentMethods: ["card", "us_bank_account"],
      },
    }

    this.initializeForm()
    this.initializeTabs()

    this.currentPageRef = null
    this.hideOrShowBillingTab = this.hideOrShowBillingTab.bind(this)
    this.doUpdate = this.doUpdate.bind(this)
    this.changeTab = this.changeTab.bind(this)
    this.calculatePrice = this.calculatePrice.bind(this)
    this.startSave = this.startSave.bind(this)
    this.setCurrentPageRef = this.setCurrentPageRef.bind(this)
  }

  initializeForm() {
    if ($context.form) {
      this.state.form = {
        ...$context.form,
        rate: {
          ...$context.form.rate,
          amount: centsToDollarsRaw($context.form.rate.amount),
          taxRate: safePercentageDecimal($context.form.rate.taxRate),
        },
        namedRateSelected: isPresent($context.form.rate.name),
        contractType: $context.form.monthToMonth
          ? "month_to_month"
          : "standard",
        customBillingPeriod: !!(
          $context.form.billingStartDate || $context.form.billingEndDate
        ),
        depositEnabled: !!$context.form.depositAmount,
        depositAmount: $context.form.depositAmount / 100,
        depositTaxAmount: $context.form.depositTaxAmount * 100,
        attachments: $context.attachments || [],
        revenueCategoryId: $context.form.revenueCategoryId,
        contacts: [],
        contactBoats: [],
        selectedCustomFields: $context.form.contractSigningEnablements,
        dueDay: $context.form.rate.dueDay,
      }
    }
  }

  initializeTabs() {
    this.state.tabs = [
      TABS.itinerary,
      TABS.rate,
      TABS.billing,
      TABS.deposit,
      TABS.charges,
      TABS.fees,
      TABS.documents,
    ]

    // for now be extra cautious to only show payment methods if the following are true:
    // 1. we are working with a contract group
    if (this.isGroup()) {
      // place the paymentMethods tab after the billing tab
      const billingIndex = this.state.tabs.indexOf(TABS.billing)
      this.state.tabs.splice(billingIndex + 1, 0, TABS.paymentMethods)
    }

    if (
      ["month_to_month", undefined].includes(this.state.form.contractType) &&
      this.state.tabs.includes(TABS.billing)
    ) {
      this.state.tabs = this.state.tabs.filter((tab) => tab !== TABS.billing)
    }

    if (!$context.chargesEnabled) {
      // remove the charges tab from the tabs list if the feature isn't enabled
      this.state.tabs = this.state.tabs.filter((tab) => tab !== TABS.charges)
    }

    if (this.isContract()) {
      // prepend tabs with contactAndBoat tab for Contracts
      this.state.tabs = [TABS.contactAndBoat, ...this.state.tabs]
    }
  }

  hideOrShowBillingTab() {
    if (
      ["month_to_month", undefined].includes(this.state.form.contractType) &&
      this.state.tabs.includes(TABS.billing)
    ) {
      this.setState({
        tabs: this.state.tabs.filter((tab) => tab !== TABS.billing),
      })
    } else if (
      this.state.form.contractType === "standard" &&
      !this.state.tabs.includes(TABS.billing)
    ) {
      // billing tab comes after rate
      const indexOfRateTab = this.state.tabs.indexOf(TABS.rate)
      const updatedTabs = [...this.state.tabs]
      updatedTabs.splice(indexOfRateTab + 1, 0, TABS.billing)
      this.setState({ tabs: updatedTabs })
    }
  }

  setCurrentPageRef(element) {
    this.currentPageRef = element
  }

  componentDidUpdate(prevProps, prevState) {
    const emptyForm = Object.values(this.state.form).every(
      (val) => val == null || val === ""
    )
    if (!emptyForm && !this.state.saving) {
      window.onbeforeunload = () => {
        return ""
      }
    } else {
      window.onbeforeunload = null
    }
    this.hideOrShowBillingTab()
  }

  isGroup() {
    return this.props.type === "group"
  }

  isContract() {
    return this.props.type === "contract"
  }

  renderRightPanel() {
    switch (this.state.activeTab) {
      case TABS.itinerary:
        return (
          <ContractGroupV2Itinerary
            form={this.state.form}
            route={this.route(TABS.itinerary)}
            ref={this.setCurrentPageRef}
            update={this.doUpdate}
            saving={this.state.saving}
            incomplete={this.state.incomplete}
            isGroup={this.isGroup()}
            monthlyDisabled={
              this.state.form.namedRateSelected &&
              !this.state.form.rate.pricingStructure.includes("_month")
            }
          />
        )
      case TABS.rate:
        return (
          <ContractGroupV2Rate
            form={this.state.form}
            pricingStructures={$context.rateOptions.pricingStructures}
            route={this.route(TABS.rate)}
            ref={this.setCurrentPageRef}
            update={this.doUpdate}
            namedRates={$context.namedRates}
            saving={this.state.saving}
            incomplete={this.state.incomplete}
            revenueCategories={$context.revenueCategories}
            longTermStorageProducts={$context.longTermStorageProducts}
            pricingUrl={$context.pricingUrl}
          />
        )
      case TABS.billing:
        return (
          <ContractGroupV2Billing
            route={this.route(TABS.billing)}
            innerRef={this.setCurrentPageRef}
            form={this.state.form}
            update={this.doUpdate}
            saving={this.state.saving}
          />
        )
      case TABS.paymentMethods:
        return (
          <ContractGroupV2PaymentMethods
            route={this.route(TABS.paymentMethods)}
            innerRef={this.setCurrentPageRef}
            form={this.state.form}
            update={this.doUpdate}
            saving={this.state.saving}
          />
        )
      case TABS.deposit:
        return (
          <ContractGroupV2Deposit
            route={this.route(TABS.deposit)}
            ref={this.setCurrentPageRef}
            form={this.state.form}
            update={this.doUpdate}
            saving={this.state.saving}
          />
        )
      case TABS.charges:
        if ($context.chargesEnabled) {
          return (
            <ContractGroupV2Charges
              form={this.state.form}
              route={this.route(TABS.charges)}
              ref={this.setCurrentPageRef}
              update={this.doUpdate}
              startDate={this.state.form.startDate}
              endDate={this.state.form.endDate}
              saving={this.state.saving}
            />
          )
        } else {
          return null
        }
      case TABS.fees:
        return (
          <ContractGroupV2FeesElectricDiscounts
            form={this.state.form}
            route={this.route(TABS.fees)}
            ref={this.setCurrentPageRef}
            update={this.doUpdate}
            startDate={this.state.form.startDate}
            endDate={this.state.form.endDate}
            saving={this.state.saving}
          />
        )
      case TABS.documents:
        return (
          <ContractGroupV2Documents
            form={this.state.form}
            route={this.route(TABS.documents)}
            ref={this.setCurrentPageRef}
            update={this.doUpdate}
            startSave={this.startSave}
            documentRequirements={$context.documentRequirements}
            marinaAttachments={$context.marinaAttachments}
            editDocumentUrl={$context.editDocumentUrl}
            saving={this.state.saving}
            doUpdate={this.doUpdate}
            action={this.props.action}
          />
        )
      case TABS.contactAndBoat:
        return (
          <ContractGroupV2AddBoat
            route={this.route(TABS.contactAndBoat)}
            ref={this.setCurrentPageRef}
            form={this.state.form}
            update={this.doUpdate}
            saving={this.state.saving}
            loadingPricing={this.state.loadingPricing}
            pricingSummaryHtml={this.state.pricingSummaryHtml}
            billingSummaryHtml={this.state.billingSummaryHtml}
          />
        )
      default:
        return null
    }
  }

  canSaveGroup(state) {
    return (
      state &&
      !!state.name &&
      !!state.startDate &&
      state.startDate != null &&
      (state.contractType === "month_to_month" || !!state.endDate) &&
      state.rate &&
      state.rate.pricingStructure &&
      state.rate.amount &&
      state.billingCycle
    )
  }

  canSaveCustomerContract(state) {
    return (
      state &&
      !!state.selectedContact &&
      !!state.selectedContactBoat &&
      state.rate &&
      state.rate.pricingStructure &&
      state.rate.amount &&
      state.billingCycle &&
      (state.contractType === "month_to_month" || !!state.endDate)
    )
  }

  canSave(state) {
    if (this.isGroup()) {
      return this.canSaveGroup(state)
    }
    if (this.isContract()) {
      return this.canSaveCustomerContract(state)
    }
    return false
  }

  // swap the change tab logic to this so that we can control the main state here on tab changes
  changeTab(nextTab) {
    this.setState({
      activeTab: nextTab,
    })
  }

  route(tab) {
    const index = this.state.tabs.indexOf(tab)
    const currentIndex = index
    const nextIndex = index + 1
    const previousIndex = index - 1
    return {
      hasPrevious: currentIndex > 0,
      goPrevious: () => this.changeTab(this.state.tabs[previousIndex]),
      hasNext: currentIndex < this.state.tabs.length - 1,
      goNext: () => this.changeTab(this.state.tabs[nextIndex]),
    }
  }

  doUpdate(data) {
    // if a contract with a per_stay rate is being switched to month to month, empty the pricingStructure.
    if (
      data.contractType === "month_to_month" &&
      !this.isMonthlyPricing(this.state.form.rate.pricingStructure)
    ) {
      this.state.form.rate.pricingStructure = ""
    }
    this.setState({ form: { ...this.state.form, ...data } })
    afterSetState(this.calculatePrice)
  }

  isMonthlyPricing(pricingStructure) {
    return $context.rateOptions.pricingStructures.monthly.some(
      (object) => object.value === pricingStructure
    )
  }

  startSave() {
    const data =
      !!this.currentPageRef &&
      typeof this.currentPageRef.fetchData === "function"
        ? this.currentPageRef.fetchData()
        : {}
    const newState = { ...this.state.form, ...data }
    const canSave = this.canSave(newState)
    if (canSave) {
      this.setState({
        form: newState,
        saving: true,
        errorMessage: null,
      })
      this.saveContract(newState)
    } else {
      Amplitude.trackEvent("contracts_v2:validation_error")
      this.setState({
        form: newState,
        saving: null,
        incomplete: true,
      })
    }
  }

  saveContract(form) {
    Amplitude.trackEvent(this.props.amplitudeSaveEvent)
    let params = this.isGroup()
      ? this.groupParams(form)
      : this.contractParams(form)
    if (
      $context.duplicateId != null &&
      this.state.copyBoats &&
      $context.copyableContractsCount > 0
    ) {
      params = { ...params, copy_boats: this.state.copyBoats }
    }
    const endpoint =
      $context.duplicateId != null
        ? buildRoute($context.cloneEndpoint, {
            duplicate_id: $context.duplicateId,
          })
        : $context.saveEndpoint
    $.post(
      endpoint,
      { ...params, ...{ _method: this.props.method } },
      (data) => {
        window.location.replace(data.redirect_to)
      }
    ).fail(() => {
      this.setState({ saving: null })
      this.setState({ errorMessage: true })
    })
  }

  defaultParams(state) {
    const params = {
      space_type: state.spaceType,
      start_date: state.startDate,
      end_date: state.endDate,
      amount: dollarsToCents(state.rate.amount),
      tax_rate: state.rate.taxRate / 100 || null,
      rate_id: state.rate.id || null,
      revenue_category_id: state.revenueCategoryId,
      pricing_structure: state.rate.pricingStructure,
      billing_cycle: state.billingCycle || null,
      billing_start_date: state.billingStartDate,
      billing_end_date: state.billingEndDate,
      notes: state.notes,
      allow_direct_payment: state.allowDirectPayment,
      attachments: _.map(state.attachments, (a) => a.id),
      fees_and_discounts: isPresent(state.feesAndDiscounts)
        ? JSON.stringify(collectionKeysToSnakeCase(state.feesAndDiscounts))
        : null,
      auto_renew_enabled: state.autoRenewEnabled,
      auto_renew_interval: state.autoRenewInterval,
      insurance_requirement: state.insuranceRequirement,
      registration_requirement: state.registrationRequirement,
      deposit_amount: isPresent(state.depositEnabled)
        ? state.depositAmount
        : null,
      deposit_tax_amount: isPresent(state.depositEnabled)
        ? state.depositTaxAmount
        : null,
      pos_items_data: isPresent(state.posItemsData)
        ? JSON.stringify(collectionKeysToSnakeCase(state.posItemsData))
        : null,
      named_rate_id: state.namedRateId,
      month_to_month: state.contractType === "month_to_month",
      contract_signing_enablements: isPresent(state.selectedCustomFields)
        ? JSON.stringify(collectionKeysToSnakeCase(state.selectedCustomFields))
        : null,
      long_term_storage_product_id: state.rate.longTermStorageProductId,
      due_day: state.dueDay,
      contract_installments: isPresent(state.rate.contractInstallments)
        ? JSON.stringify(
            collectionKeysToSnakeCase(state.rate.contractInstallments)
          )
        : JSON.stringify([]),
    }
    return params
  }

  groupParams(form) {
    const params = this.defaultParams(form)
    params.accepted_payment_methods = JSON.stringify(
      form.acceptedPaymentMethods
    )
    params.name = form.name
    if (form.copyBoats) {
      params.copy_boats = form.copyBoats
    }
    return params
  }

  contractParams(form) {
    const params = this.defaultParams(form)
    params.contact_id = form.selectedContact.id
    params.contact_boat_id = form.selectedContactBoat.id
    params.pending = false
    return params
  }

  calculatePrice() {
    const form = this.state.form

    if (this.isGroup()) {
      return
    } else if (
      isBlank(form.selectedContact) ||
      isBlank(form.selectedContactBoat) ||
      isBlank(form.rate.pricingStructure) ||
      isBlank(form.rate.amount)
    ) {
      return
    }
    this.setState({ loadingPricing: true })
    const params = this.contractParams(form)

    $.post($context.priceUrl, params, (response) => {
      const newState = {
        loadingPricing: false,
        pricingSummaryHtml: response.pricing_summary_html,
        billingSummaryHtml: response.billing_summary_html,
      }
      if (params.monthToMonth) {
        newState.form = {
          ...this.state.form,
          tabTotalPrice: `${centsToDollars(response.total_price.price)}/month`,
        }
      } else {
        newState.form = {
          ...this.state.form,
          tabTotalPrice: centsToDollars(response.total_price.price),
        }
      }
      this.setState(newState)
    })
  }

  render() {
    return (
      <div className="container">
        <div className="grid grid-cols-12 gap-3">
          <div className="card col-span-12 flex items-center justify-between border-teal-400 bg-teal-100">
            <div className="ml-2 font-semibold">{this.props.title}</div>
            <div className="space-x-4">
              {!this.isGroup() &&
                this.state.incomplete &&
                !isPresent(
                  this.state.form.endDate &&
                    this.state.form.billingCycle === "month_to_month"
                ) && (
                  <span className="text-danger">
                    Name, start date, and rate required
                  </span>
                )}
              {this.isGroup() &&
                this.state.incomplete &&
                this.state.form.billingCycle === "month_to_month" && (
                  <span className="text-danger">
                    Name, start date, and rate required
                  </span>
                )}
              {this.isGroup() &&
                this.state.incomplete &&
                this.state.form.billingCycle === "installments" && (
                  <span className="text-danger">
                    Name, start date, rate, and installment type required
                  </span>
                )}
              {this.isContract() &&
                this.state.incomplete &&
                (!this.state.form.selectedContact ||
                  !this.state.form.selectedContactBoat) && (
                  <span className="text-danger">
                    Customer and boat are required
                  </span>
                )}
              {this.state.incomplete &&
                !this.state.form.rate &&
                this.state.form.rate.amount &&
                !this.state.form.rate.pricingStructure && (
                  <span className="text-danger">
                    Please select a valid rate
                  </span>
                )}
              {this.state.errorMessage && (
                <span className="text-danger">
                  Failed to save this contract{" "}
                  {this.isGroup() ? "group" : "quote"}
                </span>
              )}
              {$context.duplicateId && $context.copyableContractsCount > 0 && (
                <FormGroup
                  inputType="checkbox"
                  label={`Copy ${$context.copyableContractsCount} of ${$context.totalCompletedContracts} contracts`}
                  className="inline-block"
                  name="copyBoats"
                  value={this.state.copyBoats}
                  onChange={() =>
                    this.setState({ copyBoats: !this.state.copyBoats })
                  }
                />
              )}
              <LegacyButton
                onClick={() => location.replace($context.backUrl)}
                type="secondary"
              >
                Cancel
              </LegacyButton>
              <LegacyButton
                onClick={this.startSave}
                type="primary"
                processing={!!this.state.saving}
              >
                {this.props.action}
              </LegacyButton>
            </div>
          </div>
          <ContractLeftTabLayout
            changeTab={this.changeTab}
            form={this.state.form}
            tabs={this.state.tabs}
            activeTab={this.state.activeTab}
            isGroup={this.isGroup()}
            namedRates={$context.namedRates}
          />
          <div className="card col-span-12 p-8 sm:col-span-7">
            {this.renderRightPanel()}
          </div>
        </div>
      </div>
    )
  }
}

ContractGroupV2Controller.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  action: PropTypes.string,
  method: PropTypes.string,
  amplitudeSaveEvent: PropTypes.string,
}

export default ContractGroupV2Controller
